<script lang="ts">
  import { db } from '../../lib/firebase';
  import { authState } from '../../lib/auth.svelte';
  import {
    doc,
    collection,
    query,
    orderBy,
    onSnapshot,
    addDoc,
    updateDoc,
    deleteDoc,
    setDoc,
    getDoc,
    increment,
    serverTimestamp,
  } from 'firebase/firestore';
  import { onMount } from 'svelte';
  import {
    FEATURE_REQUEST_STATUSES,
    FEATURE_REQUEST_CATEGORIES,
    type FeatureRequest,
    type FeatureRequestReply,
    type FeatureRequestStatus,
  } from '../../lib/types';
  import Button from '../ui/Button.svelte';

  type Props = {
    request: FeatureRequest;
    onBack: () => void;
  };

  let { request, onBack }: Props = $props();

  let replies = $state<FeatureRequestReply[]>([]);
  let loadingReplies = $state(true);
  let newReply = $state('');
  let submittingReply = $state(false);
  let hasVoted = $state(false);
  let voting = $state(false);
  let updatingStatus = $state(false);

  // Check if current user is admin
  let isAdmin = $derived(authState.isSystemAdmin || authState.isAccountAdmin);

  onMount(() => {
    // Load replies
    const repliesRef = collection(db, 'featureRequests', request.id, 'replies');
    const q = query(repliesRef, orderBy('createdAt', 'asc'));

    const unsubscribeReplies = onSnapshot(q, (snapshot) => {
      replies = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      })) as FeatureRequestReply[];
      loadingReplies = false;
    });

    // Check vote status
    if (authState.user) {
      checkUserVote();
    }

    return () => unsubscribeReplies();
  });

  async function checkUserVote() {
    if (!authState.user) return;
    try {
      const voteRef = doc(db, 'featureRequestVotes', request.id, 'votes', authState.user.uid);
      const voteDoc = await getDoc(voteRef);
      hasVoted = voteDoc.exists();
    } catch (err) {
      console.error('Error checking vote:', err);
    }
  }

  async function handleVote() {
    if (!authState.user || voting) return;

    voting = true;
    try {
      const voteRef = doc(db, 'featureRequestVotes', request.id, 'votes', authState.user.uid);
      const requestRef = doc(db, 'featureRequests', request.id);

      if (hasVoted) {
        await deleteDoc(voteRef);
        await updateDoc(requestRef, { voteCount: increment(-1) });
        hasVoted = false;
      } else {
        await setDoc(voteRef, { oderId: authState.user.uid, vote: 1, votedAt: new Date() });
        await updateDoc(requestRef, { voteCount: increment(1) });
        hasVoted = true;
      }
    } catch (err) {
      console.error('Error voting:', err);
    } finally {
      voting = false;
    }
  }

  async function handleSubmitReply() {
    if (!authState.user || !newReply.trim() || submittingReply) return;

    submittingReply = true;
    try {
      const repliesRef = collection(db, 'featureRequests', request.id, 'replies');
      await addDoc(repliesRef, {
        authorId: authState.user.uid,
        authorName: authState.user.displayName || authState.user.email?.split('@')[0] || 'User',
        authorType: isAdmin ? 'admin' : 'user',
        message: newReply.trim(),
        createdAt: serverTimestamp(),
      });
      newReply = '';
    } catch (err) {
      console.error('Error adding reply:', err);
    } finally {
      submittingReply = false;
    }
  }

  async function handleStatusChange(newStatus: FeatureRequestStatus) {
    if (!isAdmin || updatingStatus) return;

    updatingStatus = true;
    try {
      const requestRef = doc(db, 'featureRequests', request.id);
      await updateDoc(requestRef, {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      // Update local state
      request.status = newStatus;
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      updatingStatus = false;
    }
  }

  function getStatusInfo(status: string) {
    return FEATURE_REQUEST_STATUSES.find(s => s.id === status) || FEATURE_REQUEST_STATUSES[0];
  }

  function getCategoryLabel(categoryId: string) {
    return FEATURE_REQUEST_CATEGORIES.find(c => c.id === categoryId)?.label || categoryId;
  }

  function formatDate(date: Date | string) {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
</script>

<div class="detail-container">
  <!-- Header -->
  <div class="detail-header">
    <button class="back-btn" onclick={onBack}>
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clip-rule="evenodd" />
      </svg>
      Back to list
    </button>
  </div>

  <!-- Main Content -->
  <div class="detail-content">
    <div class="request-header">
      <div class="vote-section">
        <button
          class="vote-btn"
          class:voted={hasVoted}
          onclick={handleVote}
          disabled={voting || !authState.user}
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clip-rule="evenodd" />
          </svg>
          <span>{request.voteCount}</span>
        </button>
      </div>

      <div class="title-section">
        <h1>{request.title}</h1>
        <div class="meta">
          <span class="category">{getCategoryLabel(request.category)}</span>
          <span class="author">Submitted by {request.authorName}</span>
          <span class="date">{formatDate(request.createdAt)}</span>
        </div>
      </div>

      <div class="status-section">
        {#if isAdmin}
          <select
            class="status-select"
            value={request.status}
            onchange={(e) => handleStatusChange(e.currentTarget.value as FeatureRequestStatus)}
            disabled={updatingStatus}
          >
            {#each FEATURE_REQUEST_STATUSES as status}
              <option value={status.id}>{status.label}</option>
            {/each}
          </select>
        {:else}
          <span class="status-badge" style="background: {getStatusInfo(request.status).color}">
            {getStatusInfo(request.status).label}
          </span>
        {/if}
      </div>
    </div>

    <div class="description">
      <h2>Description</h2>
      <p>{request.description}</p>
    </div>

    <!-- Replies Section -->
    <div class="replies-section">
      <h2>Discussion ({replies.length})</h2>

      {#if loadingReplies}
        <div class="loading-replies">Loading...</div>
      {:else if replies.length === 0}
        <p class="no-replies">No comments yet. Be the first to share your thoughts!</p>
      {:else}
        <div class="replies-list">
          {#each replies as reply (reply.id)}
            <div class="reply" class:admin={reply.authorType === 'admin'}>
              <div class="reply-header">
                <span class="reply-author">
                  {reply.authorName}
                  {#if reply.authorType === 'admin'}
                    <span class="admin-badge">EQUOS Team</span>
                  {/if}
                </span>
                <span class="reply-date">{formatDate(reply.createdAt)}</span>
              </div>
              <p class="reply-message">{reply.message}</p>
            </div>
          {/each}
        </div>
      {/if}

      <!-- Reply Form -->
      {#if authState.user}
        <div class="reply-form">
          <textarea
            bind:value={newReply}
            placeholder="Add your comment..."
            rows="3"
            disabled={submittingReply}
          ></textarea>
          <Button
            variant="primary"
            onclick={handleSubmitReply}
            disabled={!newReply.trim() || submittingReply}
          >
            {submittingReply ? 'Posting...' : 'Post Comment'}
          </Button>
        </div>
      {:else}
        <p class="sign-in-prompt">Sign in to join the discussion</p>
      {/if}
    </div>
  </div>
</div>

<style>
  .detail-container {
    max-width: 800px;
  }

  .detail-header {
    margin-bottom: 1.5rem;
  }

  .back-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: none;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.875rem;
    color: #666;
    cursor: pointer;
    transition: all 0.2s;
  }

  .back-btn:hover {
    border-color: #667eea;
    color: #667eea;
  }

  .back-btn svg {
    width: 16px;
    height: 16px;
  }

  .detail-content {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .request-header {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .vote-section {
    flex-shrink: 0;
  }

  .vote-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 0.75rem 1rem;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    color: #6b7280;
  }

  .vote-btn:hover:not(:disabled) {
    border-color: #667eea;
    color: #667eea;
  }

  .vote-btn.voted {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-color: transparent;
    color: white;
  }

  .vote-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .vote-btn svg {
    width: 24px;
    height: 24px;
  }

  .vote-btn span {
    font-size: 1rem;
    font-weight: 600;
  }

  .title-section {
    flex: 1;
    min-width: 0;
  }

  .title-section h1 {
    margin: 0 0 0.5rem;
    font-size: 1.5rem;
    color: #333;
    line-height: 1.3;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    font-size: 0.8rem;
    color: #999;
  }

  .category {
    background: #f3f4f6;
    padding: 0.125rem 0.5rem;
    border-radius: 4px;
    color: #666;
  }

  .status-section {
    flex-shrink: 0;
  }

  .status-select {
    padding: 0.5rem 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;
    cursor: pointer;
  }

  .status-badge {
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    color: white;
  }

  .description {
    margin-bottom: 2rem;
  }

  .description h2 {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    margin: 0 0 0.75rem;
  }

  .description p {
    margin: 0;
    font-size: 0.9375rem;
    color: #555;
    line-height: 1.7;
    white-space: pre-wrap;
  }

  .replies-section h2 {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    margin: 0 0 1rem;
  }

  .loading-replies,
  .no-replies {
    color: #999;
    font-size: 0.9rem;
    text-align: center;
    padding: 1.5rem;
  }

  .replies-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .reply {
    padding: 1rem;
    background: #f9fafb;
    border-radius: 8px;
    border-left: 3px solid #e5e7eb;
  }

  .reply.admin {
    background: #f0f4ff;
    border-left-color: #667eea;
  }

  .reply-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .reply-author {
    font-weight: 500;
    color: #333;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .admin-badge {
    padding: 0.125rem 0.375rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 600;
  }

  .reply-date {
    font-size: 0.75rem;
    color: #999;
  }

  .reply-message {
    margin: 0;
    font-size: 0.9rem;
    color: #555;
    line-height: 1.6;
    white-space: pre-wrap;
  }

  .reply-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
  }

  .reply-form textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.9rem;
    font-family: inherit;
    resize: vertical;
    min-height: 80px;
  }

  .reply-form textarea:focus {
    outline: none;
    border-color: #667eea;
  }

  .reply-form :global(button) {
    align-self: flex-end;
  }

  .sign-in-prompt {
    text-align: center;
    color: #999;
    font-size: 0.9rem;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 6px;
  }

  @media (max-width: 640px) {
    .request-header {
      flex-direction: column;
    }

    .vote-section {
      order: 2;
    }

    .vote-btn {
      flex-direction: row;
      width: 100%;
      justify-content: center;
    }

    .status-section {
      order: 1;
      align-self: flex-start;
    }
  }
</style>
